import { ChangeEvent, FormEvent, useRef, useState } from "react";
import { Loader } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import { knowledgeApi } from "../../../api/knowledgeApi";
import { SupabaseKnowledgeDocument } from "../../../types/supabase";

const categories = [
  { value: "flashcards", label: "Flashcards" },
  { value: "articles", label: "Articles" },
  { value: "videos", label: "Videos" },
];

export const UploadDocument = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(categories[0].value);
  const [url, setUrl] = useState("");
  const [createdAt, setCreatedAt] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  const notify = () =>
    toast("Uploaded successfully! Redirecting...", {
      onClose: (reason: string | boolean | undefined) => {
        if (reason === true) {
          // User clicked the close button
          return;
        }
        navigate("/coordinator/knowledge-manager");
      },
    });

  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const fileExtension = file?.name.split(".").pop() || "";

    const documentRecord: SupabaseKnowledgeDocument = {
      // TODO: Generate UUID deterministically from the name and created_at
      uuid: crypto.randomUUID(),
      name: name + "." + fileExtension,
      description: description,
      category,
      url,
    };

    setUploadStatus("uploading");

    const uploadedDocument = await knowledgeApi.uploadDocument(
      documentRecord,
      file!
    );
    if (uploadedDocument === "success") {
      setUploadStatus("success");
      notify();
    } else {
      setUploadStatus("error");
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="text-2xl font-bold mb-6">
        Upload a New Knowledge Document
      </h1>
      <form
        className="rounded-xl border bg-white shadow-sm p-6 space-y-6"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              className="w-full rounded-lg border px-3 py-2 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              className="w-full rounded-lg border px-3 py-2 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              className="w-full rounded-lg border px-3 py-2 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL
            </label>
            <input
              type="url"
              className="w-full rounded-lg border px-3 py-2 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Created At
            </label>
            <input
              type="date"
              className="w-full rounded-lg border px-3 py-2 focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]"
              value={createdAt}
              onChange={(e) => setCreatedAt(e.target.value)}
              required
            />
          </div>
        </div>
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 transition-colors ${
            dragActive
              ? "border-[#4CAF50] bg-green-50"
              : "border-gray-300 bg-gray-50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{ cursor: "pointer" }}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
          />
          {file ? (
            <div className="text-green-700 font-medium">{file.name}</div>
          ) : (
            <div className="text-gray-500">
              Drag & drop a file here, or{" "}
              <span className="text-[#4CAF50] underline">browse</span>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-[#4CAF50] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#45a049] transition-colors"
          >
            {uploadStatus === "uploading" ? (
              <Loader className="animate-spin" />
            ) : (
              "Upload"
            )}
          </button>
        </div>
      </form>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
      />
    </div>
  );
};
